import React from "react";
import { X } from "lucide-react";

import { FollowButton } from "./ScreenerSearch";
import {
  dataDateText,
  formatCell,
  lookupText,
  nameFontSize,
  formatPrice,
  isSortable,
  nextSort,
  onlyFunds,
  toneOf,
  visibleColumns,
  totalPages,
  typeLabel,
} from "./screenerModel";
import styles from "./screenerPage.module.css";

const ALL_CLASSES = 3;

// 「理解为」：条件标签可以单独去掉（去掉后走 /run，不再调 LLM）；类别标签只是回显，不能去——
// 去掉「A股」而条件还是 PE，后端会立刻又解析回 A股，那个 × 等于骗人。
function ConditionBar({ understood, columns, total, dataDate, dataNote, busy, free, onRemove }) {
  // 三类都查（没限定类别）时不显示类别标签：「A股 ETF 场外基金」三个标签只是噪音
  const asked = understood?.asset_classes ?? [];
  const classes = asked.length < ALL_CLASSES ? asked : [];
  const conditions = understood?.conditions ?? [];
  const sort = understood?.sort;
  const dateText = dataDateText(dataDate, dataNote);
  // 什么都没理解出来（条件、类别、排序都没有）：不摆一条只有「理解为：」的空栏，只留下面的说明
  if (!classes.length && !conditions.length && !sort && !understood?.limit) return null;
  return (
    <div className={styles.condBar}>
      <span className={styles.condLabel}>理解为：</span>
      {classes.map((type) => <span key={type} className={`${styles.cond} ${styles.condStatic}`} title="标的类别">{typeLabel(type)}</span>)}
      {conditions.map((condition, index) => (
        <span key={`${condition.field}-${index}`} className={styles.cond}>
          {condition.label}
          <button type="button" disabled={busy} onClick={() => onRemove(index)} aria-label={`去掉条件：${condition.label}`} title="去掉这个条件">
            <X size={13} aria-hidden="true" />
          </button>
        </span>
      ))}
      {sort && <span className={`${styles.cond} ${styles.condStatic}`}>按{columns.find((c) => c.field === sort.field)?.label ?? sort.field}{sort.order === "asc" ? "升序" : "降序"}</span>}
      {understood?.limit ? <span className={`${styles.cond} ${styles.condStatic}`}>前 {understood.limit} 只</span> : null}
      <small className={styles.condMeta}>共 {total} 只{dateText ? ` · ${dateText}` : ""}{free ? " · 免费搜索" : ""}</small>
    </div>
  );
}

function Unsupported({ items }) {
  if (!items.length) return null;
  return (
    <ul className={styles.unsupported} aria-label="没能转成条件的部分">
      {items.map((item, index) => (
        <li key={`${item.phrase}-${index}`}>
          <strong>「{item.phrase}」</strong> {item.reason}
        </li>
      ))}
    </ul>
  );
}

function SortButton({ label, field, sort, busy, onSort }) {
  if (!isSortable(field)) return <span>{label}</span>;
  const active = sort?.field === field;
  const direction = active ? (sort.order === "asc" ? "升序" : "降序") : "未排序";
  return (
    <button type="button" className={`${styles.sortButton} ${active ? styles.sortActive : ""}`} disabled={busy} onClick={() => onSort(nextSort(sort, field))} aria-label={`按${label}排序，当前${direction}`}>
      {label} <span aria-hidden="true">{active && sort.order === "asc" ? "↑" : "↓"}</span>
    </button>
  );
}

function ResultTable({ result, sort, busy, held, watched, followBusy, onSort, onFollow, onOpen }) {
  const funds = onlyFunds(result.items);
  const extra = visibleColumns(result.columns, result.items);
  return (
    <div className={`${styles.tableWrap} ${busy ? styles.dim : ""}`} aria-busy={busy}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>标的</th>
            <th className={styles.priceCol}>
              {funds ? (
                // 场外基金没有当日涨跌（净值 T+1），只放净值，不留一列永远是 -- 的「涨跌」
                <SortButton label="净值" field="price" sort={sort} busy={busy} onSort={onSort} />
              ) : (
                <span className={styles.split}>
                  <SortButton label="价格" field="price" sort={sort} busy={busy} onSort={onSort} />
                  <i aria-hidden="true">|</i>
                  <SortButton label="涨跌" field="pct_chg" sort={sort} busy={busy} onSort={onSort} />
                </span>
              )}
            </th>
            {extra.map((column) => (
              <th key={column.field} className={column.condition ? styles.condCol : ""}>
                <SortButton label={column.label} field={column.field} sort={sort} busy={busy} onSort={onSort} />
                {column.note && <small>{column.note}</small>}
              </th>
            ))}
            <th className={styles.actionCol}>操作</th>
          </tr>
        </thead>
        <tbody>
          {result.items.map((item) => (
            <tr key={item.code} className={styles.row} onClick={() => onOpen(item.code)}>
              <td>
                <span className={styles.assetLink}>
                  <button type="button" className={styles.assetName} title={item.name} style={{ fontSize: nameFontSize(item.name) }} onClick={(event) => { event.stopPropagation(); onOpen(item.code); }}>{item.name}</button>
                  <small>{item.code} · {typeLabel(item.type)}</small>
                </span>
              </td>
              <td>
                <b>{formatPrice(item.price, item.type)}</b>
                {!funds && <small className={styles[toneOf("pct_chg", item.pct_chg)]}>{formatCell("pct_chg", item.pct_chg)}</small>}
              </td>
              {extra.map((column) => (
                <td key={column.field} className={`${column.condition ? styles.condCol : ""} ${styles[toneOf(column.field, item[column.field])] ?? ""}`}>
                  {formatCell(column.field, item[column.field])}
                </td>
              ))}
              <td className={styles.actionCol}>
                <FollowButton code={item.code} held={held.has(item.code) || item.held} watched={watched.has(item.code) || item.watched} busy={followBusy.has(item.code)} onFollow={onFollow} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Pager({ result, busy, onPage }) {
  const pages = totalPages(result);
  if (pages <= 1) return null;
  return (
    <nav className={styles.pager} aria-label="翻页">
      <button type="button" disabled={busy || result.page <= 1} onClick={() => onPage(result.page - 1)}>上一页</button>
      <span>{result.page} / {pages}</span>
      <button type="button" disabled={busy || result.page >= pages} onClick={() => onPage(result.page + 1)}>下一页</button>
    </nav>
  );
}

export function Skeleton({ label = "正在理解你的问题" }) {
  return (
    <section className={styles.card} role="status" aria-label={label}>
      <div className={styles.condBar}><span className={styles.condLabel}>{label}…</span></div>
      <div className={styles.skeleton} aria-hidden="true"><i style={{ width: "60%" }} /><i /><i /><i style={{ width: "80%" }} /></div>
    </section>
  );
}

export function ScreenerResults({ state, held, watched, followBusy, onRemoveCondition, onSort, onPage, onFollow, onOpen }) {
  const { understood, unsupported, result, dataDate, dataNote, busy, sort, error } = state;
  if (!result) return null;
  const empty = result.total === 0;
  return (
    <section className={styles.card} aria-label="查找结果">
      <ConditionBar understood={understood} columns={result.columns} total={result.total} dataDate={dataDate} dataNote={dataNote} busy={busy} free={Boolean(state.usage?.free)} onRemove={onRemoveCondition} />
      <Unsupported items={unsupported} />
      {state.searching && <p className={styles.searching} role="status">正在搜索</p>}
      {error && <p className={styles.inlineError} role="alert">{error}</p>}
      {empty ? (
        <div className={styles.emptyBox}>
          {understood?.conditions?.length
            ? <p>{lookupText(understood) ? `没有找到「${lookupText(understood)}」，请检查名称或代码。` : "没有同时满足这些条件的标的，请减少或放宽条件。"}</p>
            : <p>没有可以筛选的条件，请换个说法，比如写明具体数字：<br />「市盈率低于 15 的银行股」。</p>}
        </div>
      ) : (
        <ResultTable result={result} sort={sort} busy={busy} held={held} watched={watched} followBusy={followBusy} onSort={onSort} onFollow={onFollow} onOpen={onOpen} />
      )}
      {!empty && <Pager result={result} busy={busy} onPage={onPage} />}
    </section>
  );
}
